import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import useAPI from '../../api';
import {
  MenuItem,
  ListItemIcon,
  Avatar,
  Divider,
  Fab,
  Paper,
} from '@mui/material';
import { useAtom, useSetAtom } from 'jotai';
import LogoutIcon from '@mui/icons-material/Logout';
import PasswordIcon from '@mui/icons-material/Password';
import DialerSipRoundedIcon from '@mui/icons-material/DialerSipRounded';
import OpenInBrowserRoundedIcon from '@mui/icons-material/OpenInBrowserRounded';
import PinIcon from '@mui/icons-material/Pin';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate } from 'react-router-dom';
import {
  userDataAtom,
  openSnackbarAdviseAtom,
  selectedCustomerIdAtom,
} from '../../Atoms';
import CustomMenu from '../CustomMenu';
import DeviceDialog from './DeviceDialog';
import DefaultPageDialog from './DefaultPageDialog';
import keysToLowerCase from '../../Helpers/keysToLowerCase';

const AccountMenu = function AccountMenu({ onLogout, userRoles }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const intl = useIntl();
  const API = useAPI();
  const setCustomerId = useSetAtom(selectedCustomerIdAtom);
  const [userData, setUserData] = useAtom(userDataAtom);
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const [devices, setDevices] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deviceDialog, setDeviceDialog] = useState(false);
  const [deviceNotFound, setDeviceNotFound] = useState(false);
  const [defaultPageDialog, setDefaultPageDialog] = useState(false);

  const handleOpenDeviceDialog = () => {
    setDeviceNotFound(false);
    setDeviceDialog(true);
  };
  const handleCloseDeviceDialog = () => {
    setDeviceNotFound(false);
    setDeviceDialog(false);
  };

  const handleOpenDefaultPageDialog = () => {
    setDefaultPageDialog(true);
  };
  const handleCloseDefaultPageDialog = () => {
    setDefaultPageDialog(false);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSetVoIPDevice = (voIPDevice, silent = false) => {
    if (userData && userRoles)
      API.setUserVoIPDevices(
        userData?.id,
        voIPDevice,
        () => {
          setUserData({ ...userData, voIPDevice });
          if (!silent) {
            handleCloseDeviceDialog();
            openSnackbar({
              message: intl.messages.DEVICE_SETTED,
              severity: 'success',
            });
          }
        },
        () => {
          if (!silent)
            openSnackbar({
              message: intl.messages.ERROR_DEVICE_SETTINGS,
              severity: 'error',
            });
        }
      );
  };

  useEffect(() => {
    if (userData?.id)
      API.getUserVoIPDevices(
        userData.id,
        (res) => {
          setDevices(res);
          if (userData?.voIPDevice) {
            // DB stores the full id (e.g. sip:303@host:port;rinstance=...).
            // Match a list device by its base part (before ;rinstance).
            const baseDevice = userData.voIPDevice.split(';')[0];
            const selectedDevice = res.find(({ device_id }) =>
              device_id?.startsWith(baseDevice)
            );
            if (!selectedDevice) {
              // Stored device is no longer available: prompt a new selection.
              setDeviceNotFound(true);
              setDeviceDialog(true);
            } else if (selectedDevice.device_id !== userData.voIPDevice)
              // The rinstance (second part) changed: persist the new full id
              // silently (no snackbar) so the stored device stays current.
              handleSetVoIPDevice(selectedDevice.device_id, true);
          }
        },
        () => {
          setDevices([]);
        }
      );
    // Keyed to the user id only: depending on the whole userData object loops
    // because handleSetVoIPDevice -> setUserData creates a new userData ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const updateDefaultPage = (defaultPage) => {
    API.updateUserExtensionData(
      'defaultPage',
      defaultPage,
      () => {
        setUserData({
          ...userData,
          extensionData: JSON.stringify({
            ...keysToLowerCase(JSON.parse(userData?.extensionData)),
            defaultPage,
          }),
        });
        handleCloseDefaultPageDialog();
        openSnackbar({
          message: intl.messages.DEFAULT_PAGE_SETTED,
          severity: 'success',
        });
      },
      () => {
        openSnackbar({
          message: intl.messages.ERROR_DEFAULT_PAGE_SETTINGS,
          severity: 'error',
        });
      }
    );
  };

  const open = Boolean(anchorEl);
  const extensionData = keysToLowerCase(JSON.parse(userData?.extensionData));
  return (
    <>
      <DefaultPageDialog
        open={defaultPageDialog}
        onClose={handleCloseDefaultPageDialog}
        defaultValue={extensionData?.defaultPage ?? 'PRIORITIES'}
        onSubmit={updateDefaultPage}
        userRoles={userRoles}
      />
      <DeviceDialog
        open={deviceDialog}
        onClose={handleCloseDeviceDialog}
        defaultDevice={userData?.voIPDevice}
        onSubmit={handleSetVoIPDevice}
        userId={userData?.id}
        devices={devices}
        notFound={deviceNotFound}
      />
      <Paper sx={{ borderRadius: '50px' }}>
        <Fab
          onClick={handleClick}
          variant="extended"
          sx={{
            padding: '6px',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            background: 'transparent',
            color: 'inherit',
            '&:hover': {
              background: 'transparent',
            },
          }}
        >
          <Avatar
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': { cursor: 'pointer' },
              color: '#fff',
            }}
          >
            {userData?.userName?.substring(0, 1)?.toUpperCase()}
          </Avatar>
          {`${userData.name} ${userData.surname}`}
          <KeyboardArrowDownIcon />
        </Fab>
        <CustomMenu
          open={open}
          anchor={anchorEl}
          close={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <div>
            {userData?.extension && (
              <MenuItem
                onClick={() => {
                  handleOpenDeviceDialog();
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <DialerSipRoundedIcon fontSize="small" />
                </ListItemIcon>
                {`${intl.messages.DEVICE} ${userData?.voIPDevice ? `(${devices?.find(({ device_id }) => device_id === userData?.voIPDevice)?.user_agent})` : ''}`}
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleOpenDefaultPageDialog();
                handleClose();
              }}
            >
              <ListItemIcon>
                <OpenInBrowserRoundedIcon fontSize="small" />
              </ListItemIcon>
              {`${intl.messages.DEFAULT_PAGE}: ${extensionData?.defaultPage ? intl.messages[extensionData?.defaultPage?.toUpperCase()] : intl.messages.UNAVAILABLE}`}
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate('/changePassword');
                handleClose();
              }}
            >
              <ListItemIcon>
                <PasswordIcon fontSize="small" />
              </ListItemIcon>
              {intl.messages.CHANGE_PASSWORD}
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate('/setup2FA');
                handleClose();
              }}
            >
              <ListItemIcon>
                <PinIcon fontSize="small" />
              </ListItemIcon>
              {intl.messages.TWO_FACTOR_SETUP}
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate('/setupFIDO2');
                handleClose();
              }}
            >
              <ListItemIcon>
                <PinIcon fontSize="small" />
              </ListItemIcon>
              {intl.messages.WEBAUTHN_SETUP}
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                onLogout();
                navigate('/'); //login page
                handleClose();
                setCustomerId('');
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {intl.messages.LOGOUT}
            </MenuItem>
          </div>
        </CustomMenu>
      </Paper>
    </>
  );
};

AccountMenu.propTypes = {
  onLogout: PropTypes.func.isRequired,
  userRoles: PropTypes.array,
  redemptionReached: PropTypes.bool,
};
export default AccountMenu;
